import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Check, ArrowLeft } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { cn } from "@/lib/utils";

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";

  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setToken((prev) => prev || tokenFromUrl);
  }, [tokenFromUrl]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("auth.errorPasswordsDontMatch"));
      return;
    }
    if (password.length < 8) {
      setError(t("auth.errorPasswordMinLength"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiFetch("/api/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, newPassword: password }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; message?: string };
      if (!res.ok) {
        setError(data.message ?? t("auth.errorResetFailed"));
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch {
      setError(t("auth.errorForgot"));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-xl font-bold text-foreground">{t("auth.invalidLink")}</h1>
          <p className="text-muted-foreground">{t("auth.invalidLinkDesc")}</p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("auth.requestNewLink")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div
        className={cn(
          "hidden md:flex md:w-[40%] flex-col justify-center px-10 lg:px-16",
          "bg-[hsl(var(--login-brand-bg))] text-white"
        )}
      >
        <h2 className="text-2xl font-bold mb-8">TPT Brány</h2>
        <p className="text-lg leading-relaxed text-white/90 max-w-md">{t("auth.brandText")}</p>
      </div>

      <div className="flex-1 flex flex-col justify-center bg-background px-6 py-12 md:px-12 lg:px-20">
        <div className="w-full max-w-md mx-auto">
          <div className="md:hidden mb-8">
            <h2 className="text-xl font-bold text-foreground">TPT Brány</h2>
            <p className="text-sm text-muted-foreground mt-2">{t("auth.brandText")}</p>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">{t("auth.resetTitle")}</h1>
          <p className="text-muted-foreground mb-8">{t("auth.resetIntro")}</p>

          {success ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-4 flex items-start gap-3">
                <Check className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <p className="text-sm text-green-800 dark:text-green-300">{t("auth.resetSuccess")}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <input type="hidden" name="token" value={token} />
              <div>
                <label htmlFor="reset-password" className="block text-sm font-medium text-foreground mb-1">
                  {t("auth.newPassword")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="reset-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.placeholderPasswordMin")}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="reset-confirm" className="block text-sm font-medium text-foreground mb-1">
                  {t("auth.confirmPassword")}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="reset-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t("auth.placeholderConfirm")}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {submitting ? t("auth.saveSubmitting") : t("auth.savePassword")}
              </button>
            </form>
          )}

          <Link
            to="/login"
            className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("auth.backToLogin")}
          </Link>

          <p className="mt-12 text-center text-sm text-muted-foreground">{t("auth.copyright")}</p>
        </div>
      </div>
    </div>
  );
}
